import React, { useEffect, useState } from "react";
import {
  CButton,
  CCard,
  CRow,
  CCol,
  CCardHeader,
  CForm,
  CCardBody,
  CCardFooter,
  CFormLabel,
  CFormInput,
  CFormSelect,
  CBadge,
  CFormTextarea,
  CSpinner,
} from "@coreui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { insert, update, remove, getStoreDetails, getUplines } from "src/services/Stores";
import CIcon from "@coreui/icons-react";
import { cilTrash, cilArrowLeft, cilCalendar, cilMap } from "@coreui/icons";
import { Toaster } from "src/components/Toaster";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getProvinces, getCities, getDistricts } from "src/services/Region";

const storeOptions = [
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "C", label: "C" },
  { value: "D", label: "D" },
];

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});
function StoreActions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [provinceOption, setProvinceOption] = useState([]);
  const [cityOption, setCityOption] = useState([]);
  const [districtOption, setDistrictOption] = useState([]);
  const [uplineOption, setUplineOption] = useState([]);

  // Form state with all required fields
  const [formData, setFormData] = useState({
    store_id: "",
    name: "",
    store_type: "A",
    address: "",
    phone: "",
    province_id: "",
    provinceName: "",
    city_id: "",
    cityName: "",
    district_id: "",
    districtName: "",
    latitude: "0",
    longitude: "0",
    upline_name: "",
    vaporista_name: "",
    vaporista_phone: "",
    vaporista_birthday: null,
    owner_name: "",
    owner_phone: "",
    owner_birthday: null,
    open_date: null,
  });

  const [loading, setLoading] = useState({
    page: !!id,
    submit: false,
    delete: false,
  });
  const [validated, setValidated] = useState(false);
  const [toaster, setToaster] = useState({
    open: false,
    message: "",
    color: "success",
  });
  const [mapCenter, setMapCenter] = useState([-6.2088, 106.8456]); // Default to Jakarta
  const [showMap, setShowMap] = useState(false);

  const isEdit = !!id;

  // Fetch store details if in edit mode
  useEffect(() => {
    if (!isEdit) return;

    const fetchStoreData = async () => {
      try {
        const response = await getStoreDetails(id);
        const store = response.store;

        setFormData({
          store_id: store.store_id || "",
          name: store.name || "",
          store_type: store.store_type || "A",
          address: store.address || "",
          phone: store.phone || "",
          province_id: store.province_id || "",
          provinceName: store.provinceName || "",
          city_id: store.city_id || "",
          cityName: store.cityName || "",
          district_id: store.district_id || "",
          districtName: store.districtName || "",
          latitude: store.latitude || "0",
          longitude: store.longitude || "0",
          upline_name: store.upline_name || "",
          vaporista_name: store.vaporista_name || "",
          vaporista_phone: store.vaporista_phone || "",
          vaporista_birthday: store.vaporista_birthday ? new Date(store.vaporista_birthday) : null,
          owner_name: store.owner_name || "",
          owner_phone: store.owner_phone || "",
          owner_birthday: store.owner_birthday ? new Date(store.owner_birthday) : null,
          open_date: store.open_date ? new Date(store.open_date) : null,
        });

        // Set map center if coordinates exist
        if (store.latitude && store.longitude) {
          setMapCenter([parseFloat(store.latitude), parseFloat(store.longitude)]);
        }
      } catch (error) {
        setToaster({
          open: true,
          message: "Failed to load store data",
          color: "danger",
        });
      } finally {
        setLoading((prev) => ({ ...prev, page: false }));
      }
    };

    fetchStoreData();
  }, [id, isEdit]);

  useEffect(() => {
    fetchProvinces();
    fetchUplines();
  }, []);

  const fetchUplines = async () => {
    const upline = await getUplines();
    setUplineOption(upline.data.map(upline => ({label: upline.name, value: upline.id})))
  }
  const fetchProvinces = async () => {
    const provinces = await getProvinces();
    setProvinceOption(provinces.map(province => ({label: province.nama, value: province.id})))
  }

  useEffect(() => {
    if (formData.province_id) {
      fetchCities(formData.province_id);
    }
  }, [formData.province_id]);

  const fetchCities = async (province_id) => {
    const cities = await getCities(province_id);
    setCityOption(cities.map(city => ({label: city.nama, value: city.id})))
  }

  useEffect(() => {
    if (formData.city_id) {
      fetchDistricts(formData.city_id);
    }
  }, [formData.city_id]);

  const fetchDistricts = async (city_id) => {
    const districts = await getDistricts(city_id);
    setDistrictOption(districts.map(district => ({label: district.nama, value: district.id})))
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date, field) => {
    setFormData((prev) => ({ ...prev, [field]: date }));
  };

  const handleMapClick = (e) => {
    const { lat, lng } = e.latlng;
    setFormData((prev) => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString(),
    }));
    setMapCenter([lat, lng]);
    setShowMap(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    setLoading((prev) => ({ ...prev, submit: true }));

    try {
      // Prepare data for API
      const data = {
        ...formData,
        id: isEdit ? id : undefined,
        vaporista_birthday: formData.vaporista_birthday?.toISOString(),
        owner_birthday: formData.owner_birthday?.toISOString(),
        open_date: formData.open_date?.toISOString(),
      };

      const response = isEdit ? await update(data) : await insert(data);

      if (response?.data?.success) {
        setToaster({
          open: true,
          message: response.data.message || (isEdit ? "Store updated successfully" : "Store created successfully"),
          color: "success",
        });

        setTimeout(() => navigate(-1), 1500);
      } else {
        throw new Error(response?.data?.message || "Operation failed");
      }
    } catch (error) {
      setToaster({
        open: true,
        message: error.message || "An error occurred",
        color: "danger",
      });
    } finally {
      setLoading((prev) => ({ ...prev, submit: false }));
    }
  };

  // Map click handler component
  function MapClickHandler({ onClick }) {
    useMapEvents({
      click: onClick,
    });
    return null;
  }
  const handleDelete = async () => {
    // Confirm before deleting
    if (!window.confirm("Are you sure you want to delete this store? This action cannot be undone.")) {
      return;
    }

    setLoading((prev) => ({ ...prev, delete: true }));

    try {
      const response = await remove(id);

      if (response?.data?.success) {
        setToaster({
          open: true,
          message: response.data.message || "Store deleted successfully",
          color: "success",
        });

        // Navigate back after successful deletion
        setTimeout(() => navigate("/stores"), 1500);
      } else {
        throw new Error(response?.data?.message || "Failed to delete store");
      }
    } catch (error) {
      setToaster({
        open: true,
        message: error.message || "An error occurred while deleting the store",
        color: "danger",
      });
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  if (loading.page) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <CSpinner color="primary" />
      </div>
    );
  }

  return (
    <div>
      <CCard>
        <Toaster
          open={toaster.open}
          message={toaster.message}
          setOpen={(val) => setToaster((prev) => ({ ...prev, open: val }))}
          color={toaster.color}
        />

        <CCardHeader>
          <CRow className="align-items-center">
            <CCol md={6}>
              <h5>{isEdit ? "Edit Store" : "Create New Store"}</h5>
            </CCol>
            <CCol md={6} className="text-end">
              <CButton color="light" onClick={() => navigate(-1)} disabled={loading.submit || loading.delete}>
                <CIcon icon={cilArrowLeft} className="me-2" />
                Back
              </CButton>
            </CCol>
          </CRow>
        </CCardHeader>

        <CCardBody>
          <CForm onSubmit={handleSubmit} noValidate validated={validated}>
            <CRow className="mb-3">
              <CCol md={6}>
                <CFormLabel>
                  Store Name <CBadge color="danger">Required</CBadge>
                </CFormLabel>
                <CFormInput
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  feedbackInvalid="Please provide a store name"
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel>Store Type</CFormLabel>
                <CFormSelect 
                  name="store_type"
                  value={storeOptions.find(option => option.value === formData.store_type)?.value || "A"}
                  onChange={handleChange}
                  options={storeOptions}
                >
                </CFormSelect>
              </CCol>
            </CRow>

            <CRow className="mb-3">
              <CCol md={12}>
                <CFormLabel>Address</CFormLabel>
                <CFormTextarea name="address" value={formData.address} onChange={handleChange} rows={2} />
              </CCol>
            </CRow>

            <CRow className="mb-3">
              <CCol md={6}>
                <CFormLabel>Phone Number</CFormLabel>
                <CFormInput type="text" name="phone" value={formData.phone} onChange={handleChange} />
              </CCol>

              <CCol md={6}>
                <CFormLabel>Open Date</CFormLabel>
                <div className="d-flex align-items-center">
                  <DatePicker
                    selected={formData.open_date}
                    onChange={(date) => handleDateChange(date, "open_date")}
                    className="form-control"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select open date"
                  />
                  <CIcon icon={cilCalendar} className="ms-2 text-primary" />
                </div>
              </CCol>
            </CRow>

            <CRow className="mb-3">
              <CCol md={6}>
                <CFormLabel>Province</CFormLabel>
                {/* <CFormInput type="text" name="provinceName" value={formData.provinceName} onChange={handleChange} /> */}
                <CFormSelect
                  name="province_id"
                  placeholder="Select Province"
                  value={provinceOption.find(option => option.value === parseInt(formData.province_id))}
                  options={provinceOption}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  onChange={handleChange}
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel>City</CFormLabel>
                {/* <CFormInput type="text" name="cityName" value={formData.cityName} onChange={handleChange} /> */}
                <CFormSelect
                  name="city_id"
                  placeholder="Select City"
                  value={cityOption.find(option => option.value === parseInt(formData.city_id))}
                  options={cityOption}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  onChange={handleChange}
                  disabled={!formData.province_id}
                />
              </CCol>
            </CRow>

            <CRow className="mb-3">
              <CCol md={6}>
                <CFormLabel>District</CFormLabel>
                {/* <CFormInput type="text" name="districtName" value={formData.districtName} onChange={handleChange} /> */}
                <CFormSelect
                  name="district_id"
                  placeholder="Select District"
                  value={districtOption.find(option => option.value === parseInt(formData.district_id))}
                  options={districtOption}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  onChange={handleChange}
                  disabled={!formData.city_id}
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel>Upline Name</CFormLabel>
                {/* <CFormInput type="text" name="upline_name" value={formData.upline_name} onChange={handleChange} /> */}
                <CFormSelect
                  name="upline_name"
                  placeholder="Select District"
                  value={uplineOption.find(option => option.value === parseInt(formData.upline_name))}
                  options={uplineOption}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  onChange={handleChange}
                />
              </CCol>
            </CRow>

            <CRow className="mb-3">
              <CCol>
                <CFormLabel>Location Coordinates</CFormLabel>
                <div className="d-flex align-items-center mb-2">
                  <CFormInput
                    type="text"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    placeholder="Latitude"
                    className="me-2"
                  />
                  <CFormInput
                    type="text"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    placeholder="Longitude"
                  />
                  <CButton color="light" onClick={() => setShowMap(!showMap)} className="ms-2">
                    <CIcon icon={cilMap} />
                  </CButton>
                </div>

                {showMap && (
                  <div
                    style={{
                      height: "300px",
                      borderRadius: "0.375rem",
                      overflow: "hidden",
                      position: "relative", // Add this
                      zIndex: 0, // Add this
                    }}
                  >
                    <MapContainer
                      center={mapCenter}
                      zoom={15}
                      style={{
                        height: "100%",
                        width: "100%",
                        position: "relative", // Add this
                      }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={mapCenter}>
                        <Popup>Store Location</Popup>
                      </Marker>
                      <MapClickHandler onClick={handleMapClick} />
                    </MapContainer>
                  </div>
                )}
              </CCol>
            </CRow>

            <CRow className="mb-3">
              <CCol md={6}>
                <h5>Vaporista Information</h5>
                <div className="mb-3">
                  <CFormLabel>Name</CFormLabel>
                  <CFormInput
                    type="text"
                    name="vaporista_name"
                    value={formData.vaporista_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <CFormLabel>Phone</CFormLabel>
                  <CFormInput
                    type="text"
                    name="vaporista_phone"
                    value={formData.vaporista_phone}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <CFormLabel>Birthday</CFormLabel>
                  <div className="d-flex align-items-center">
                    <DatePicker
                      selected={formData.vaporista_birthday}
                      onChange={(date) => handleDateChange(date, "vaporista_birthday")}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select birthday"
                    />
                    <CIcon icon={cilCalendar} className="ms-2 text-primary" />
                  </div>
                </div>
              </CCol>

              <CCol md={6}>
                <h5>Owner Information</h5>
                <div className="mb-3">
                  <CFormLabel>Name</CFormLabel>
                  <CFormInput type="text" name="owner_name" value={formData.owner_name} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <CFormLabel>Phone</CFormLabel>
                  <CFormInput type="text" name="owner_phone" value={formData.owner_phone} onChange={handleChange} />
                </div>
                <div>
                  <CFormLabel>Birthday</CFormLabel>
                  <div className="d-flex align-items-center">
                    <DatePicker
                      selected={formData.owner_birthday}
                      onChange={(date) => handleDateChange(date, "owner_birthday")}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select birthday"
                    />
                    <CIcon icon={cilCalendar} className="ms-2 text-primary" />
                  </div>
                </div>
              </CCol>
            </CRow>

            <CCardFooter className="text-end">
              {isEdit && (
                <CButton
                  color="danger"
                  className="me-2"
                  onClick={handleDelete}
                  disabled={loading.submit || loading.delete}
                >
                  {loading.delete ? (
                    <CSpinner size="sm" />
                  ) : (
                    <>
                      <CIcon icon={cilTrash} className="me-2" />
                      Delete Store
                    </>
                  )}
                </CButton>
              )}

              <CButton
                color="secondary"
                className="me-2"
                onClick={() => navigate(-1)}
                disabled={loading.submit || loading.delete}
              >
                Cancel
              </CButton>

              <CButton color="primary" type="submit" disabled={loading.submit || loading.delete}>
                {loading.submit ? <CSpinner size="sm" /> : isEdit ? "Update Store" : "Create Store"}
              </CButton>
            </CCardFooter>
          </CForm>
        </CCardBody>
      </CCard>
    </div>
  );
}

export default StoreActions;
